import React from 'react';
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import Classnames from 'classnames';
import {
  extent,
  scaleLinear,
  scaleBand,
  line,
  format,
} from 'd3';

import './style.css';

const data = (_, props) => props.data;
const x = (_, props) => props.x;
const y = (_, props) => props.y;

const width = (_, props) => props.width;
const gutter = state => state.gutter || 0;
const totalSize = createSelector([width], width => [width, 500]);
const size = createSelector(
  [totalSize, gutter],
  (totalSize, gutter) => 
    [ totalSize[0], (totalSize[1] - gutter) / 2 ]
);
const margin = (_, props) => props.margin || { left: 40, top: props.title ? 60 : 10, bottom: 10 };

const xScale = createSelector(
  [data, x, size, margin],
  (data, x, size, margin) =>
    scaleBand()
      .domain(['before','after'])
      .range([margin.left || 0, size[0] - (margin.right || 0)])
      .paddingInner(0)
      .paddingOuter(0.4)
);

const yScale = createSelector(
  [data, y, size, margin],
  (data, y, size, margin) => 
    scaleLinear()
      .domain(extent([0, ...data.map(d => d[y])]))
      .nice(4)
      .clamp(true)
      .range([size[1] - (margin.bottom || 0), margin.top || 0])
);

const XAxis = ({ scale }) => (
  <g className="axis x-axis">
    <line x1={scale.range()[0]} x2={scale.range()[1]} />
  </g>
);

const YAxis = ({ scale, gridWidth }) => (
  <g className="axis y-axis">
    <g key={0} transform={`translate(0,${scale(0)})`}>
      <line x2={-5} />
      <line x2={gridWidth} className="grid" />
      <text x={-5} dx={-2} textAnchor="end" dy="0.35em">
        {"0%"}
      </text>
    </g>
  </g>
);

const BarGraph = props => (
  <svg width={props.totalSize[0]} height={props.totalSize[1] / 2}>
    <g className="timeline">
      {props.title && <text y={30} className="title">{props.title}</text>}
      
      <g>
        <g transform={`translate(${props.xScale.range()[0]},0)`}>
          <YAxis
            scale={props.yScale}
            gridWidth={props.xScale.range()[1] - props.xScale.range()[0]} />
        </g>

        <g transform={`translate(0,${props.yScale(0)})`}>
          <XAxis scale={props.xScale} />
        </g>
      </g>

      <g className="bar-graph">
        {props.data.map((d,i) => {
          const y = props.yScale(d[props.y]) || 0,
            zero = props.yScale(0);
          const compare = props.data[0][props.y] - d[props.y]
          const portfolio = d.portfolio === "before" || d.portfolio === "after"
          const tooShort = Math.abs(y - zero) < 20;
          const negative = d[props.y] < 0;
          return (<g key={i}>
            {portfolio
              ? <rect
                className="rect"
                key={d[props.x]}
                x={props.xScale(d[props.x])}
                width={props.xScale.bandwidth()}
                y={negative ? zero : y} 
                height={negative ? y - zero : zero - y}
                fill={props.colorScale(d[props.x])}/>
              : null}
            {!!d[props.y] && portfolio
              ? <text
                className={Classnames("text", { "outside": tooShort })}
                x={props.xScale(d[props.x])+.5*props.xScale.bandwidth()}
                y={tooShort 
                  ? y + (negative ? 17 : -5)
                  : props.yScale(.5*d[props.y]) + 6}>
                {format(props.yFormat)(d[props.y])}</text>
              : null}
            {i > 0 && portfolio
              ? <text
                className="compare"
                x={props.xScale(d[props.x])+.5*props.xScale.bandwidth()}
                y={y + (negative 
                  ? 16 + (tooShort ? 15 : 0)
                  : -8 + (tooShort ? -15 : 0)) }>
                {compare === 0 
                  ? "Equal" 
                  : compare < 0 
                    ? format(props.yFormat)(Math.abs(compare)) + " over"
                    : format(props.yFormat)(Math.abs(compare)) + " under"}
                </text>
              : null}
            {portfolio
              ? null
              : <g className="benchmark"
                  transform={`translate(0,${y})`}>
                  <line 
                    x1={props.xScale.range()[0]} 
                    y1="0" 
                    x2={props.xScale.range()[1]} 
                    y2="0"></line>
                  <text className="compare">
                    <tspan dy={negative ? 3 : 2}>{format(props.yFormat)(d[props.y])}</tspan>
                    <tspan x={0} dy={-11}>{d.portfolio}</tspan>
                  </text>
                </g>}
          </g>);
        })}
      </g>

    </g>
  </svg>
);

const mapStateToProps = createStructuredSelector({
  data,
  x,
  y,
  xScale,
  yScale,
  totalSize,
  gutter,
});

export default connect(mapStateToProps)(BarGraph);



// WEBPACK FOOTER //
// ./src/components/BarGraph/index.js